import type { CSSProperties, HTMLAttributes, ReactNode } from "react";

/**
 * TheCore Studio — Badge / status pill.
 * Tone maps to the semantic status palette.
 */
export type BadgeTone = "neutral" | "brand" | "success" | "warning" | "danger" | "info";
export type BadgeVariant = "soft" | "solid";

export interface BadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "style"> {
  children?: ReactNode;
  /** Semantic color. @default 'neutral' */
  tone?: BadgeTone;
  /** @default 'soft' */
  variant?: BadgeVariant;
  /** Leading status dot. */
  dot?: boolean;
  style?: CSSProperties;
}

const TONES: Record<BadgeTone, { fg: string; bg: string; solid: string }> = {
  neutral: { fg: "var(--text-muted)", bg: "var(--surface-muted)", solid: "var(--clay-700)" },
  brand: { fg: "var(--brand-press)", bg: "var(--brand-soft)", solid: "var(--brand)" },
  success: { fg: "var(--status-success-fg)", bg: "var(--status-success-bg)", solid: "var(--sage-700)" },
  warning: { fg: "var(--status-warning-fg)", bg: "var(--status-warning-bg)", solid: "var(--honey-500)" },
  danger: { fg: "var(--status-danger-fg)", bg: "var(--status-danger-bg)", solid: "var(--rust-500)" },
  info: { fg: "var(--status-info-fg)", bg: "var(--status-info-bg)", solid: "var(--slate-500)" },
};

export function Badge({
  children,
  tone = "neutral",
  variant = "soft",
  dot = false,
  style = {},
  ...rest
}: BadgeProps) {
  const t = TONES[tone] ?? TONES.neutral;
  const solid = variant === "solid";

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        height: 24,
        padding: "0 10px",
        borderRadius: "var(--radius-pill)",
        font: "var(--fw-semibold) var(--fs-2xs)/1 var(--font-body)",
        letterSpacing: "var(--ls-wide)",
        textTransform: "uppercase",
        color: solid ? "#fff" : t.fg,
        background: solid ? t.solid : t.bg,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {dot && (
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 999,
            background: solid ? "#fff" : t.solid,
          }}
        />
      )}
      {children}
    </span>
  );
}
